import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, ErrorBox, LoadingSpinner } from '../components/Section';
import { useSession } from '../hooks/useSession';
import { formatDateTime } from '../utils';

export function OperationsRunbook() {
  const { session } = useSession();
  const [crons, setCrons] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});

  const refresh = () => {
    callApi('/api/v1/admin/system/crons', { accessToken: session.access_token })
      .then(setCrons)
      .catch((e) => setError(e.message));
  };

  useEffect(refresh, [session.access_token]);

  const runNow = async (name) => {
    if (!window.confirm(`Run "${name}" now? This is synchronous and may take a while.`)) return;
    setRunning(name);
    setError('');
    try {
      const data = await callApi(
        `/api/v1/admin/system/crons/${name}/run-now`,
        { method: 'POST', accessToken: session.access_token }
      );
      setResults((prev) => ({ ...prev, [name]: data }));
      refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(null);
    }
  };

  if (error && !crons) return <ErrorBox message={error} />;
  if (!crons) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-stone-800">Operations runbook</h1>

      <ErrorBox message={error} />

      <Section>
        <p className="text-xs text-stone-500 mb-3">
          Schedules are owned by Railway; this list is descriptive. "Run now"
          calls the same service methods Railway hits, synchronously.
        </p>
        <div className="divide-y">
          {crons.crons.map((c) => {
            const result = results[c.name];
            return (
              <div key={c.name} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-stone-800">{c.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      <span className="font-mono">{c.cron_expression}</span>
                      {' · next: '}
                      {formatDateTime(c.next_run_at)}
                    </div>
                    <div className="text-sm text-stone-600 mt-1">{c.description}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      Steps: {c.steps.join(' → ')}
                    </div>
                  </div>
                  <button
                    disabled={running === c.name}
                    onClick={() => runNow(c.name)}
                    className="text-xs bg-stone-800 text-white rounded px-3 py-1 disabled:opacity-50 whitespace-nowrap"
                  >
                    {running === c.name ? 'Running…' : 'Run now'}
                  </button>
                </div>
                {result && (
                  <div className={`mt-2 text-xs rounded p-2 ${
                    result.status === 'succeeded'
                      ? 'bg-green-50 text-green-800'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    <div className="font-medium">
                      {result.status} in {(result.duration_ms / 1000).toFixed(1)}s
                    </div>
                    {result.error && <div className="mt-1">{result.error}</div>}
                    {result.summary && (
                      <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px]">
                        {JSON.stringify(result.summary, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
